/**
 * Inference Admin screen — operator's view of the GPU compute marketplace.
 *
 * Three tabs (Overview / Customers / Jobs). The customer-facing twin
 * is console.nurev.io. Same backend, different lens: this view shows
 * all customers + the ops actions (mint key, revoke key, monitor job
 * stream, see provider/model split) the customer-facing console
 * intentionally doesn't expose.
 *
 * Money is microcents on the wire (1/10,000,000 of a USD cent). The
 * display layer converts to dollars. The legacy view used 7 decimal
 * places; we round to 4 since anything below $0.0001 is noise to a
 * staff member glancing at the queue.
 */
import { FormEvent, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import {
  CreateCustomerInput,
  InferenceJob,
  InferenceCustomer,
  InferenceApiKey
} from '@/api/inferenceAdmin';
import {
  useCreateInferenceApiKey,
  useCreateInferenceCustomer,
  useInferenceCustomer,
  useInferenceCustomers,
  useInferenceJobs,
  useInferenceUsage,
  useRevokeInferenceApiKey
} from '@/hooks/useInferenceAdmin';
import {
  ActionButton,
  BackButton,
  Badge,
  Card,
  Pagination,
  SectionLabel,
  Skeleton,
  StatCard
} from '@/components/ui/atoms';
import { fmtDate, fmtDateTime, fmtNum } from '@/lib/format';

const fmtMicrocents = (mc?: number): string => {
  if (!mc || mc < 0) return '$0.0000';
  return `$${(mc / 10_000_000).toFixed(4)}`;
};

const fmtCents = (c?: number): string => {
  if (!c || c < 0) return '$0.00';
  return `$${(c / 100).toFixed(2)}`;
};

const fmtTokens = (t?: number): string => {
  if (t == null) return '—';
  if (t >= 1_000_000) return `${(t / 1_000_000).toFixed(1)}M`;
  if (t >= 1_000) return `${(t / 1_000).toFixed(1)}K`;
  return String(t);
};

// ─── Overview tab ─────────────────────────────────────────────────────────

function OverviewTab() {
  const { data, isLoading } = useInferenceUsage(7);
  const totals = data?.totals || {
    totalRequests: 0,
    totalTokens: 0,
    totalCost: 0,
    totalRevenue: 0
  };
  const margin = totals.totalRevenue - totals.totalCost;
  const marginPct =
    totals.totalRevenue > 0
      ? Math.round((margin / totals.totalRevenue) * 100)
      : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard
          label="Requests (7d)"
          value={fmtNum(totals.totalRequests)}
          loading={isLoading}
        />
        <StatCard
          label="Tokens (7d)"
          value={fmtTokens(totals.totalTokens)}
          loading={isLoading}
        />
        <StatCard
          label="Cost (7d)"
          value={fmtMicrocents(totals.totalCost)}
          sub="Paid to providers"
          loading={isLoading}
        />
        <StatCard
          label="Revenue (7d)"
          value={fmtMicrocents(totals.totalRevenue)}
          sub={
            marginPct !== null
              ? `${fmtMicrocents(margin)} margin · ${marginPct}%`
              : 'Billed to customers'
          }
          color="text-emerald-300"
          loading={isLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <SectionLabel>By Provider (7d)</SectionLabel>
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : !data?.byProvider?.length ? (
            <p className="text-sm text-nurev-muted">No traffic this week.</p>
          ) : (
            <div className="space-y-2">
              {data.byProvider.map(p => (
                <div
                  key={p._id || 'unknown'}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="capitalize text-nurev-text">{p._id || '—'}</span>
                  <span className="text-nurev-muted">
                    {fmtNum(p.requests)} requests · {fmtTokens(p.tokens)} tokens
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <SectionLabel>Top Models (7d)</SectionLabel>
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : !data?.byModel?.length ? (
            <p className="text-sm text-nurev-muted">No models served.</p>
          ) : (
            <div className="space-y-2">
              {data.byModel.slice(0, 8).map(m => (
                <div
                  key={m._id || 'unknown'}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="font-mono text-xs text-nurev-text">
                    {m._id || '—'}
                  </span>
                  <span className="text-nurev-muted">{fmtTokens(m.tokens)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

// ─── Customers tab ────────────────────────────────────────────────────────

function CustomersTab({
  onSelect
}: {
  onSelect: (id: string) => void;
}) {
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const create = useCreateInferenceCustomer();
  const { data, isLoading } = useInferenceCustomers({ page, limit: 25 });
  const customers = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const input: CreateCustomerInput = {
      name: String(fd.get('name') || ''),
      email: String(fd.get('email') || ''),
      companyName: String(fd.get('companyName') || '') || undefined
    };
    try {
      const res = await create.mutateAsync(input);
      setNewKey(res.apiKey);
      setShowCreate(false);
    } catch (err) {
      // Surface the error inline rather than throwing
      console.error('create customer failed', err);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-nurev-text">
          Inference Customers
        </h3>
        <ActionButton size="sm" onClick={() => setShowCreate(s => !s)}>
          {showCreate ? 'Cancel' : '+ Add Customer'}
        </ActionButton>
      </div>

      {newKey && <OneTimeKeyBanner apiKey={newKey} onDismiss={() => setNewKey(null)} />}

      {showCreate && (
        <Card>
          <form onSubmit={handleCreate} className="space-y-3">
            <FormField name="name" placeholder="Name *" required />
            <FormField name="email" placeholder="Email *" type="email" required />
            <FormField name="companyName" placeholder="Company (optional)" />
            <div className="flex gap-2">
              <ActionButton
                type="submit"
                size="sm"
                disabled={create.isPending}
              >
                {create.isPending ? 'Creating…' : 'Create'}
              </ActionButton>
              <ActionButton
                variant="neutral"
                size="sm"
                onClick={() => setShowCreate(false)}
              >
                Cancel
              </ActionButton>
            </div>
          </form>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 text-right font-medium">Monthly Spend</th>
                <th className="px-4 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-nurev-border/50">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : customers.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-12 text-center text-nurev-muted"
                  >
                    No customers yet.
                  </td>
                </tr>
              ) : (
                customers.map(c => (
                  <CustomerRow key={c._id} c={c} onSelect={onSelect} />
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="border-t border-nurev-border px-4 py-3">
            <Pagination
              page={page}
              totalPages={totalPages}
              onChange={setPage}
            />
          </div>
        )}
      </Card>
    </div>
  );
}

function CustomerRow({
  c,
  onSelect
}: {
  c: InferenceCustomer;
  onSelect: (id: string) => void;
}) {
  return (
    <tr
      onClick={() => onSelect(c._id)}
      className="cursor-pointer border-b border-nurev-border/50 transition-colors hover:bg-white/[0.02]"
    >
      <td className="px-4 py-3 text-nurev-text">
        {c.name}
        {c.testAccount && (
          <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-medium text-amber-300 ring-1 ring-amber-500/30">
            TEST
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-nurev-muted">{c.email}</td>
      <td className="px-4 py-3">
        <Badge value={c.status} />
      </td>
      <td className="px-4 py-3 capitalize text-nurev-muted">
        {c.billingPlan || '—'}
      </td>
      <td className="px-4 py-3 text-right text-nurev-text">
        {fmtCents(c.monthlySpend)}
      </td>
      <td className="px-4 py-3 text-xs text-nurev-muted">
        {fmtDate(c.createdAt)}
      </td>
    </tr>
  );
}

// ─── Customer detail ──────────────────────────────────────────────────────

function CustomerDetail({
  id,
  onBack
}: {
  id: string;
  onBack: () => void;
}) {
  const { data, isLoading } = useInferenceCustomer(id);
  const createKey = useCreateInferenceApiKey();
  const revokeKey = useRevokeInferenceApiKey();
  const [newKey, setNewKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const customer = data?.customer;
  const keys = data?.apiKeys || [];
  const usage = data?.usage30d || {
    totalRequests: 0,
    totalTokens: 0,
    totalCostMicrocents: 0,
    totalPriceMicrocents: 0
  };

  const handleNewKey = async () => {
    setActionError(null);
    try {
      const res = await createKey.mutateAsync({
        customerId: id,
        name: 'admin-issued'
      });
      setNewKey(res.apiKey);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to create key.';
      setActionError(msg);
    }
  };

  const handleRevoke = async (keyId: string) => {
    if (!window.confirm('Revoke this API key? Any service using it will start failing.')) {
      return;
    }
    setActionError(null);
    try {
      await revokeKey.mutateAsync({ customerId: id, keyId });
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || 'Failed to revoke key.';
      setActionError(msg);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} label="Back to customers" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="space-y-4">
        <BackButton onClick={onBack} label="Back to customers" />
        <Card className="text-center">
          <p className="text-sm text-nurev-muted">Customer not found.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <BackButton onClick={onBack} label="Back to customers" />

      <Card>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-lg font-bold text-nurev-text">
              {customer.name}
              {customer.testAccount && (
                <span className="ml-2 rounded bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-medium text-amber-300 ring-1 ring-amber-500/30">
                  TEST
                </span>
              )}
            </h3>
            <p className="text-sm text-nurev-muted">
              {customer.email}
              {customer.companyName ? ` — ${customer.companyName}` : ''}
            </p>
          </div>
          <Badge value={customer.status} />
        </div>
        <div className="mt-3 flex flex-wrap gap-3 text-xs text-nurev-muted">
          <span>Plan: {customer.billingPlan || '—'}</span>
          <span>RPM: {fmtNum(customer.rateLimitRpm)}</span>
          <span>TPM: {fmtNum(customer.rateLimitTpm)}</span>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <StatCard
          label="Requests (30d)"
          value={fmtNum(usage.totalRequests)}
        />
        <StatCard label="Tokens (30d)" value={fmtTokens(usage.totalTokens)} />
        <StatCard
          label="Cost (30d)"
          value={fmtMicrocents(usage.totalCostMicrocents)}
        />
        <StatCard
          label="Revenue (30d)"
          value={fmtMicrocents(usage.totalPriceMicrocents)}
          color="text-emerald-300"
        />
      </div>

      {newKey && (
        <OneTimeKeyBanner apiKey={newKey} onDismiss={() => setNewKey(null)} />
      )}

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <SectionLabel>API Keys ({keys.length})</SectionLabel>
          <ActionButton
            size="sm"
            onClick={handleNewKey}
            disabled={createKey.isPending}
          >
            {createKey.isPending ? '…' : '+ New Key'}
          </ActionButton>
        </div>
        {actionError && (
          <p className="mb-2 text-xs text-red-300">{actionError}</p>
        )}
        {keys.length === 0 ? (
          <p className="text-sm text-nurev-muted">No keys yet.</p>
        ) : (
          <div className="space-y-2">
            {keys.map(k => (
              <ApiKeyRow
                key={k._id}
                k={k}
                onRevoke={() => handleRevoke(k._id)}
                revoking={revokeKey.isPending}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function ApiKeyRow({
  k,
  onRevoke,
  revoking
}: {
  k: InferenceApiKey;
  onRevoke: () => void;
  revoking: boolean;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-sm">
      <div>
        <span className="font-mono text-nurev-text">{k.keyPrefix}…</span>
        {k.name && (
          <span className="ml-2 text-xs text-nurev-muted">{k.name}</span>
        )}
        {k.lastUsedAt && (
          <span className="ml-2 text-[10px] text-nurev-muted-dim">
            last used {fmtDateTime(k.lastUsedAt)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <Badge value={k.status} />
        {k.status === 'active' && (
          <button
            disabled={revoking}
            onClick={onRevoke}
            className="text-xs text-red-300 transition hover:text-red-200 disabled:opacity-30"
          >
            Revoke
          </button>
        )}
      </div>
    </div>
  );
}

function OneTimeKeyBanner({
  apiKey,
  onDismiss
}: {
  apiKey: string;
  onDismiss: () => void;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
      <div className="flex-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-300">
          API key created — shown once
        </p>
        <code className="mt-1 block select-all break-all font-mono text-sm text-emerald-200">
          {apiKey}
        </code>
        <p className="mt-1 text-[10px] text-emerald-300/70">
          Copy it now. The plaintext is never persisted server-side; we only
          store a bcrypt hash.
        </p>
      </div>
      <button
        onClick={onDismiss}
        aria-label="Dismiss"
        className="rounded p-1 text-emerald-300 hover:bg-emerald-500/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ─── Jobs tab ─────────────────────────────────────────────────────────────

function JobsTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useInferenceJobs({ page, limit: 50 });
  const jobs = data?.docs || [];
  const totalPages = data?.totalPages || 1;

  return (
    <Card className="overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-nurev-border text-left text-[10px] uppercase tracking-wider text-nurev-muted">
              <th className="px-3 py-3 font-medium">Type</th>
              <th className="px-3 py-3 font-medium">Model</th>
              <th className="px-3 py-3 font-medium">Provider</th>
              <th className="px-3 py-3 font-medium">Status</th>
              <th className="px-3 py-3 text-right font-medium">Tokens</th>
              <th className="px-3 py-3 text-right font-medium">Latency</th>
              <th className="px-3 py-3 text-right font-medium">Cost</th>
              <th className="px-3 py-3 text-right font-medium">Revenue</th>
              <th className="px-3 py-3 font-medium">Time</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 8 }).map((_, i) => (
                <tr key={i} className="border-b border-nurev-border/50">
                  {Array.from({ length: 9 }).map((_, j) => (
                    <td key={j} className="px-3 py-3">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : jobs.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="px-4 py-12 text-center text-nurev-muted"
                >
                  No jobs yet.
                </td>
              </tr>
            ) : (
              jobs.map(j => <JobRow key={j._id} j={j} />)
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="border-t border-nurev-border px-4 py-3">
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      )}
    </Card>
  );
}

function JobRow({ j }: { j: InferenceJob }) {
  return (
    <tr className="border-b border-nurev-border/50 hover:bg-white/[0.02]">
      <td className="px-3 py-2 text-xs text-nurev-muted">
        {j.type === 'embeddings' ? 'Embed' : 'Chat'}
      </td>
      <td className="max-w-[200px] truncate px-3 py-2 font-mono text-xs text-nurev-text">
        {j.model}
      </td>
      <td className="px-3 py-2">
        <span className="rounded bg-purple-500/15 px-2 py-0.5 text-[10px] font-medium capitalize text-purple-300 ring-1 ring-purple-500/30">
          {j.provider || '—'}
        </span>
      </td>
      <td className="px-3 py-2">
        <Badge value={j.status} />
      </td>
      <td className="px-3 py-2 text-right text-nurev-text">
        {fmtNum(j.totalTokens)}
      </td>
      <td className="px-3 py-2 text-right text-xs text-nurev-muted">
        {j.latencyMs ? `${j.latencyMs}ms` : '—'}
      </td>
      <td className="px-3 py-2 text-right text-xs text-nurev-muted">
        {fmtMicrocents(j.costMicrocents)}
      </td>
      <td className="px-3 py-2 text-right text-xs text-emerald-300">
        {fmtMicrocents(j.priceMicrocents)}
      </td>
      <td className="px-3 py-2 text-xs text-nurev-muted">
        {fmtDateTime(j.createdAt)}
      </td>
    </tr>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'customers', label: 'Customers' },
  { id: 'jobs', label: 'Jobs' }
] as const;
type Tab = (typeof TABS)[number]['id'];

export function InferenceAdminScreen() {
  const [tab, setTab] = useState<Tab>('overview');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
            Inference Admin
          </h1>
          <p className="mt-1 text-xs text-nurev-muted">
            GPU compute marketplace. Customer + key management, usage
            aggregates, live job stream. Customer-facing twin lives at{' '}
            <a
              href="https://console.nurev.io"
              target="_blank"
              rel="noreferrer"
              className="text-amber-300 hover:text-amber-200"
            >
              console.nurev.io
            </a>
            .
          </p>
        </div>
        <a
          href="https://app.nurev.io/inference"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-nurev-muted hover:text-amber-300"
          title="Open the legacy view on the publisher dashboard"
        >
          legacy view
          <ExternalLink className="h-3 w-3" />
        </a>
      </header>

      {!selectedId && (
        <div className="flex w-fit gap-1 rounded-lg bg-white/[0.03] p-1">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`rounded-md px-4 py-2 text-xs font-medium transition ${
                tab === t.id
                  ? 'bg-amber-500 text-nurev-bg'
                  : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {selectedId ? (
        <CustomerDetail id={selectedId} onBack={() => setSelectedId(null)} />
      ) : (
        <>
          {tab === 'overview' && <OverviewTab />}
          {tab === 'customers' && <CustomersTab onSelect={setSelectedId} />}
          {tab === 'jobs' && <JobsTab />}
        </>
      )}
    </div>
  );
}

// ─── Tiny form input ─────────────────────────────────────────────────────

function FormField({
  name,
  placeholder,
  type = 'text',
  required
}: {
  name: string;
  placeholder: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <input
      name={name}
      type={type}
      placeholder={placeholder}
      required={required}
      className="w-full rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-2 text-sm text-nurev-text placeholder:text-nurev-muted-dim focus:border-amber-500/50 focus:outline-none"
    />
  );
}
